/**
 * ログイン状態の管理。認証基盤の状態変化を購読し、利用者マスタの情報と合わせて配る。
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { configureApi } from '../api/client.js';
import { endpoints } from '../api/endpoints.js';
import type { MeResponse } from '../api/types.js';
import { AuthContext, type AuthState } from './context.js';
import { getAuthAdapter, type AuthUser } from './identity.js';

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const adapter = useMemo(() => getAuthAdapter(), []);
  const [account, setAccount] = useState<AuthUser | null>(null);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    configureApi({
      getToken: () => adapter.getToken(),
      onUnauthenticated: () => {
        // トークンは有効でも利用者マスタから外れている場合があるため、状態を落とす
        setMe(null);
      },
    });
  }, [adapter]);

  const loadMe = useCallback(async () => {
    try {
      setMe(await endpoints.me());
      setError(null);
    } catch (cause) {
      setMe(null);
      setError(cause instanceof Error ? cause.message : 'ログイン情報を取得できませんでした');
    }
  }, []);

  useEffect(() => {
    return adapter.subscribe((user) => {
      setAccount(user);
      if (!user) {
        setMe(null);
        setLoading(false);
        return;
      }
      void loadMe().finally(() => setLoading(false));
    });
  }, [adapter, loadMe]);

  const value: AuthState = {
    loading,
    account,
    me,
    error,
    signInWithPassword: async (email, password) => {
      setError(null);
      await adapter.signInWithPassword(email, password);
    },
    signInWithGoogle: async () => {
      setError(null);
      await adapter.signInWithGoogle();
    },
    signOut: async () => {
      await adapter.signOut();
      setMe(null);
    },
    refresh: loadMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
